/**
 * Hierarchy Configuration Page
 * Allows manufacturers to define custom role names for their distribution network
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hierarchyService, HierarchyLevel } from '../services/hierarchy.service';
import { Alert, Button, HierarchyTree, PageHeader, Input, type HierarchyTreeNode } from '../components/ui';

export default function HierarchyConfigPage() {
  const navigate = useNavigate();
  const [levels, setLevels] = useState<HierarchyLevel[]>([
    { levelNumber: 0, roleName: 'Dealer' },
    { levelNumber: 1, roleName: 'Sub-dealer' },
    { levelNumber: 2, roleName: 'Retailer' }
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadHierarchyConfig();
  }, []);

  const loadHierarchyConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const config = await hierarchyService.getConfig();
      
      if (config.hasConfig && config.levels.length > 0) {
        setLevels(config.levels);
      }
    } catch (err: any) {
      console.error('Failed to load hierarchy config:', err);
      setError(err.response?.data?.error || 'Failed to load hierarchy configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleNameChange = (levelNumber: number, newName: string) => {
    setLevels(levels.map(level =>
      level.levelNumber === levelNumber
        ? { ...level, roleName: newName }
        : level
    ));
  };

  const handleAddLevel = () => {
    if (levels.length >= 10) {
      setError('Maximum 10 hierarchy levels allowed');
      return;
    }
    
    const newLevel: HierarchyLevel = {
      levelNumber: levels.length,
      roleName: `Level ${levels.length}`
    };
    setLevels([...levels, newLevel]);
  };

  const handleRemoveLevel = (levelNumber: number) => {
    if (levels.length <= 1) {
      setError('At least one hierarchy level is required');
      return;
    }
    
    // Remove the level and renumber remaining levels
    const updatedLevels = levels
      .filter(level => level.levelNumber !== levelNumber)
      .map((level, index) => ({
        ...level,
        levelNumber: index
      }));
    
    setLevels(updatedLevels);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate
      for (const level of levels) {
        if (!level.roleName || level.roleName.trim() === '') {
          setError(`Role name for Level ${level.levelNumber} cannot be empty`);
          return;
        }
      }

      await hierarchyService.saveConfig(levels);
      setSuccess('Hierarchy configuration saved successfully!');
      
      // Redirect back to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/settings');
      }, 2000);
    } catch (err: any) {
      console.error('Failed to save hierarchy config:', err);
      setError(err.response?.data?.error || 'Failed to save hierarchy configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  const hierarchyPreviewNodes = useMemo<HierarchyTreeNode[]>(() => {
    const nestedChain = levels.reduceRight<HierarchyTreeNode[]>(
      (children, level) => [
        {
          id: `level-${level.levelNumber}`,
          label: level.roleName || `Level ${level.levelNumber}`,
          subtitle: `Level ${level.levelNumber}`,
          statusLabel: 'Configured',
          statusTone: 'active',
          children,
        },
      ],
      []
    );

    return [
      {
        id: 'manufacturer-root',
        label: 'Manufacturer',
        subtitle: 'Root owner',
        statusLabel: 'Anchored',
        statusTone: 'anchored',
        children: nestedChain,
      },
    ];
  }, [levels]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading hierarchy configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          className="mb-6"
          title="Hierarchy Configuration"
          description="Define custom role names for your distribution network."
          actions={
            <Button variant="outline" onClick={handleCancel} disabled={saving}>
              Back to Settings
            </Button>
          }
        />

        {/* Alerts */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">About Hierarchy Levels</p>
              <p>Define the roles in your distribution network. Level 0 is the first tier (e.g., Dealer), Level 1 is the second tier (e.g., Sub-dealer), and so on. You can have up to 10 levels.</p>
            </div>
          </div>
        </div>

        {/* Hierarchy Levels Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Distribution Levels</h2>
          
          <div className="space-y-4">
            {levels.map((level) => (
              <div key={level.levelNumber} className="flex items-center gap-4">
                <div className="flex-shrink-0 w-24">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    Level {level.levelNumber}
                  </span>
                </div>
                
                <div className="flex-1">
                  <Input
                    value={level.roleName}
                    onChange={(e) => handleRoleNameChange(level.levelNumber, e.target.value)}
                    placeholder={`Enter role name for Level ${level.levelNumber}`}
                    disabled={saving}
                  />
                </div>

                {levels.length > 1 && (
                  <button
                    onClick={() => handleRemoveLevel(level.levelNumber)}
                    disabled={saving}
                    className="flex-shrink-0 p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Remove this level"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add Level Button */}
          {levels.length < 10 && (
            <button
              onClick={handleAddLevel}
              disabled={saving}
              className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Another Level
            </button>
          )}

          {/* Example Preview */}
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Hierarchy Preview</h3>
            <HierarchyTree nodes={hierarchyPreviewNodes} />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
    </div>
  );
}
